import { Link, useParams } from 'react-router-dom'
import { ArrowRight, MapPin } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { formatFeeCents, getCityBySlug, getStateBySlug } from '@/data/shop'
import { useSeo } from '@/lib/seo'

export function ShopCityPage() {
  const { state: stateSlug, city: citySlug } = useParams<{ state: string; city: string }>()

  const state = stateSlug ? getStateBySlug(stateSlug) : undefined
  const city = stateSlug && citySlug ? getCityBySlug(stateSlug, citySlug) : undefined

  if (state && city) {
    useSeo({
      title: city.seo?.title ?? `Used Cars in ${city.name}, ${state.abbreviation} | Bespokian Auto`,
      description: city.seo?.description ?? city.description,
      image: `/shop-images/states/${state.slug}.jpg`,
    })
  }

  if (!state || !city) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-page py-16">
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="py-10">
              <h1 className="text-2xl font-bold text-gray-900">City not found</h1>
              <p className="mt-2 text-gray-600">
                We couldn’t find that city landing page. Try returning to the state page.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                {stateSlug && (
                  <Link to={`/shop/${stateSlug}`}>
                    <Button>Back to state</Button>
                  </Link>
                )}
                <Link to="/shop">
                  <Button variant="outline">Shop hub</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-page py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/shop" className="hover:underline">
              Shop
            </Link>
            <span>/</span>
            <Link to={`/shop/${state.slug}`} className="hover:underline">
              {state.name}
            </Link>
            <span>/</span>
            <span className="font-medium text-gray-900">{city.name}</span>
          </div>

          <h1 className="mt-2 text-3xl font-bold text-gray-900">
            Used Cars in {city.name}, {state.abbreviation}
          </h1>
          <p className="mt-2 text-gray-600 max-w-3xl">{city.description}</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to={`/vehicles?state=${encodeURIComponent(state.slug)}&city=${encodeURIComponent(city.slug)}`}>
              <Button size="lg">
                Browse inventory <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/apply">
              <Button size="lg" variant="outline">
                Apply for Financing
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>Regional details</span>
                    </div>
                    <p className="mt-2 text-gray-900 font-semibold">
                      Delivery: {city.deliveryDays.min}–{city.deliveryDays.max} days
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Metro population: {city.metroPopulation.toLocaleString()} · Nearest hub:{' '}
                      {city.nearestHub} ({city.hubDistance} mi)
                    </p>
                  </div>
                  <Badge variant="default">{city.vehicleCount.toLocaleString()} vehicles</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Taxes & fees (state + local)</h2>
                <p className="mt-2 text-sm text-gray-600">
                  State sales tax: {state.salesTaxRate}% · Local add-on: {city.localTaxAddon}% ·
                  Registration fee: {formatFeeCents(state.registrationFee)} · Title fee:{' '}
                  {formatFeeCents(state.titleFee)}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Popular makes</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {city.popularMakes.map((m) => (
                    <span key={m} className="badge bg-gray-100 text-gray-700">
                      {m}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-primary-50 border-primary-200">
              <CardContent>
                <h3 className="font-semibold text-gray-900">Source</h3>
                <p className="mt-2 text-sm text-gray-600">
                  This city page is rendered from the client’s Next.js content copied under `/br/`.
                </p>
                <Link to={`/shop/${state.slug}`} className="inline-block mt-4">
                  <Button>Back to {state.name}</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

